import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';

import { TableViewRow } from './types';

export const useTableViewState = <T extends TableViewRow>(initialData: T[] = [], options?: { trackRef?: boolean }) => {
  const prevInitData = useRef<T[] | null>(null);
  const [data, setData] = useState<T[]>(initialData);
  const [editRow, setEditRow] = useState<T | null>(null);
  const [selectedRow, setSelectedRow] = useState<T | null>(null);
  const [newRows, setNewRows] = useState<T[]>([]);
  const [editedRow, setEditedRow] = useState<T | null>(null);
  const [rowBackup, setRowBackup] = useState<T | null>(null);

  useEffect(() => {
    if (options?.trackRef) {
      if (!isEqual(prevInitData.current, initialData)) {
        setData(initialData);
        prevInitData.current = initialData;
      }
    } else {
      setData((prev) => {
        if (!isEqual(prev, initialData)) {
          return initialData;
        }
        return prev;
      });
    }
  }, [initialData]);

  // merged data is exposed in case parent needs context of both saved & unsaved / in-edit rows
  const mergedData = useMemo(() => {
    const idsInData = new Set(data.map((row) => row.id));
    const combined = [...data];

    // Add any new row that isn't already in the main data
    newRows.forEach((newRow) => {
      if (!idsInData.has(newRow.id)) {
        combined.push(newRow);
      }
    });

    // Replace edited row if present
    if (editedRow) {
      return combined.map((row) => (row.id === editedRow.id ? editedRow : row));
    }

    return combined;
  }, [data, newRows, editedRow]);

  const startEditRow = useCallback((row: T) => {
    setEditRow((prev) => {
      if (!prev) {
        setRowBackup(row);
      }
      return row;
    });
  }, []);

  const cancelEdit = useCallback(() => {
    if (editRow && rowBackup) {
      setEditRow(null);
      setData((prev) => prev.map((row) => (row.id === rowBackup.id ? rowBackup : row)));

      setNewRows((prev) => prev.filter(({ id }) => id !== rowBackup.id));
      setRowBackup(null);
    } else {
      setEditRow(null);
      setRowBackup(null);
    }
  }, [editRow, rowBackup]);

  const selectRow = useCallback((row?: T) => {
    setSelectedRow((prev) => (prev?.id === row?.id ? null : (row ?? null)));
  }, []);

  const updateNewRow = useCallback((rowId: string | number, columnId: keyof T, value: any) => {
    setNewRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, [columnId]: value } : row)));
  }, []);

  const updateRow = useCallback((row: T, isInternalUpdate = true) => {
    if (!isInternalUpdate) return;
    setEditedRow(row);
  }, []);

  const patchRow = useCallback((row: T) => {
    setData((prev) => prev.map((r) => (r.id === row.id ? row : r)));
    setEditedRow(row);
  }, []);

  const changeCell = useCallback(
    (rowId: string | number, columnId: keyof T, newValue: any, isInternalUpdate = true) => {
      if (!isInternalUpdate || !editRow || editRow.id !== rowId) return;
      setEditedRow({ ...editRow, [columnId]: newValue });
    },
    [editRow],
  );

  const deleteNewRow = useCallback((row: T) => {
    setNewRows((prev) => prev.filter(({ id }) => id !== row.id));
  }, []);

  const clearNewRows = useCallback(() => {
    setNewRows([]);
    setEditRow(null);
  }, []);

  const resetEditingState = useCallback(() => {
    clearNewRows();
    setEditedRow(null);
    setEditRow(null);
  }, [clearNewRows]);

  return {
    data,
    setData,
    mergedData,
    editRow,
    selectedRow,
    newRows,
    editedRow,
    rowBackup,
    startEditRow,
    cancelEdit,
    selectRow,
    updateNewRow,
    updateRow,
    patchRow,
    changeCell,
    deleteNewRow,
    clearNewRows,
    resetEditingState,
    setNewRows,
    setEditedRow,
  };
};
